import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowDown, CheckCircle, BarChart3, Package, Zap, Shield, Users, FileText } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import UnifiedHeader from "@/components/UnifiedHeader";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent, events } = useAnalytics();
  const { user } = useAuth();
  
  useEffect(() => {
    // Track landing page view with additional context
    trackEvent('landing_page_viewed', {
      referrer: document.referrer,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const handleGetStartedClick = () => {
    trackEvent(events.USER_SIGNED_UP, {
      source: 'hero_button',
      page: 'landing'
    });
  };

  const handlePricingClick = () => {
    trackEvent('pricing_viewed', {
      source: 'hero_button',
      page: 'landing'
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      {/* Header */}
      <UnifiedHeader />

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center animate-fade-in">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Order Ease Online
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold text-purple-600 mb-4">
            Your Complete Etsy Order Management Solution
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Order ease online with our powerful Etsy business management tools. 
            Streamline your Etsy orders with automated tracking, detailed analytics, 
            and comprehensive order management all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link to="/dashboard">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-lg px-8 py-4">
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/signup" onClick={handlePricingClick}>
                <Button variant="outline" size="lg" className="border-purple-600 text-purple-600 hover:bg-purple-50 text-lg px-8 py-4 animate-scale-in">
                  Start Managing Orders with Ease!
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything you need to order ease online for your Etsy store
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From order tracking to detailed reports, Order Ease Online provides all the tools 
              you need to grow your Etsy business efficiently and manage orders with ease.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover-scale cursor-pointer border-purple-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <Package className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Order Management</h3>
                <p className="text-gray-600">
                  Keep track of all your orders, customer details, and shipping information in one organized dashboard.
                </p>
              </CardContent>
            </Card>
            
            <Card className="hover-scale cursor-pointer border-purple-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <BarChart3 className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Analytics & Reports</h3>
                <p className="text-gray-600">
                  Get detailed insights into your sales performance, customer behavior, and business growth trends.
                </p>
              </CardContent>
            </Card>
            
            <Card className="hover-scale cursor-pointer border-purple-100 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <Zap className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Automated Workflows</h3>
                <p className="text-gray-600">
                  Automate repetitive tasks like order updates, customer notifications, and inventory management.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-purple-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose Order Ease Online?
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-purple-600 mt-1" />
                  <div>
                    <h3 className="font-semibold">Easy Etsy Integration</h3>
                    <p className="text-gray-600">Connect your Etsy store in seconds and start managing orders immediately.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-purple-600 mt-1" />
                  <div>
                    <h3 className="font-semibold">Real-time Sync</h3>
                    <p className="text-gray-600">All your order data stays synchronized with your Etsy store automatically.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-purple-600 mt-1" />
                  <div>
                    <h3 className="font-semibold">Powerful Analytics</h3>
                    <p className="text-gray-600">Make data-driven decisions with comprehensive reports and insights.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="animate-scale-in">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Start Your Free Trial</h3>
                  <p className="text-gray-600 mt-2">Order ease online - No credit card required</p>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>Order Management</span>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>Basic Analytics</span>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>Email Support</span>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <Link to="/signup" onClick={handleGetStartedClick}>
                    
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Package className="h-8 w-8 text-purple-400" />
                <span className="text-2xl font-bold">Order Ease Online</span>
              </div>
              <p className="text-gray-400">
                The ultimate solution for managing your Etsy orders efficiently and growing your business. 
                Order ease online with our comprehensive tools.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8">
            {/* Etsy Attribution Notice */}
            <div className="mb-4">
              <p className="text-xs text-gray-500">
                The term 'Etsy' is a trademark of Etsy, Inc. This application uses the Etsy API but is not endorsed or certified by Etsy, Inc.
              </p>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400">© 2024 OrderEaseOnline. All rights reserved.</p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
